import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ChangeDetectorRef,
  inject,
  DestroyRef,
  input,
  output,
} from '@angular/core';
import {
  UntypedFormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ToastService } from '@app/core/services/toast/toast.service';
import {
  Company,
  CreateCommentGQL,
  UserGQL,
  CommentableUnion,
  Comment,
  CreateCommentMutation,
  User,
  DeleteCommentGQL,
} from '@app/generated/graphql';
import { Apollo } from 'apollo-angular';
import { UserHelper } from '@app/core/helpers/user.helper';
import { ServerSideFileHelper } from '@app/core/helpers/server-side-file.helper';
import { NgTemplateOutlet, DatePipe } from '@angular/common';
import { Bind } from '@openng/optimus-ui/bind';
import { Button } from '@openng/optimus-ui/button';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Ag1AvatarComponent } from '../ag1-avatar/ag1-avatar.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'ag1-comment',
  templateUrl: './ag1-comment.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    Bind,
    Button,
    NgTemplateOutlet,
    AngularSvgIconModule,
    Ag1AvatarComponent,
    DatePipe,
  ],
})
export class Ag1CommentComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private formBuilder = inject(UntypedFormBuilder);
  private userGQL = inject(UserGQL);
  private apollo = inject(Apollo);
  private createCommentGQL = inject(CreateCommentGQL);
  private deleteCommentGQL = inject(DeleteCommentGQL);
  private toastService = inject(ToastService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  readonly comments = input<Comment[]>(undefined);
  readonly commentable = input<CommentableUnion>(undefined);
  readonly commentableTypeOverride = input<string | null>(null);

  readonly commentCreationEvent = output<Comment>();
  readonly commentDeletionEvent = output<number>();

  // modal
  showCommentModal: boolean = false;

  // data
  myCompany: Company;
  myUser: User;

  // form
  commentForm = this.formBuilder.group({
    comment: ['', Validators.required],
  });

  // used to generate random ids for menuPosition directive
  uniqueID: number;

  // used to generate profile picture
  userHelper = UserHelper;
  serverSideFileHelper = ServerSideFileHelper;

  isSubmitting: boolean = false;

  constructor() {
    this.uniqueID = Math.floor(Math.random() * 10000) + 1;
  }

  ngOnInit(): void {
    this.userGQL
      .fetch()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        this.myCompany = res.data.myCompany;
        this.myUser = res.data.myUser;
      });
  }

  openCommentModal(): void {
    this.showCommentModal = true;
  }

  closeCommentModal(): void {
    this.showCommentModal = false;
    this.commentForm.reset();
  }

  toggleCommentModal(): void {
    this.showCommentModal = !this.showCommentModal;
  }

  addComment(): void {
    if (this.commentForm.invalid) return;

    this.isSubmitting = true;

    this.apollo
      .mutate<CreateCommentMutation>({
        mutation: this.createCommentGQL.document,
        variables: {
          attributes: {
            userid: this.myUser.id,
            commenttext: this.commentForm.get('comment').value.trim(),
            commentableid: this.commentable().id,
            commentabletype: this.getCommentableType(),
          },
        },
      })
      .subscribe(
        (res) => {
          this.closeCommentModal();
          const comment = res.data.createComment.comment;
          comment['userId'] = this.myUser.id;
          this.comments().unshift(comment);
          this.changeDetectorRef.detectChanges();
          this.toastService.showToast('Comment added successfully');
          this.isSubmitting = false;

          // tell parent to update comments displayed
          this.commentCreationEvent.emit(comment);
        },
        (err) => {
          this.isSubmitting = false;
          console.error(err);
          this.closeCommentModal();
          this.toastService.showToast('Failed to create comment');
        },
      );
  }

  deleteComment(comment: Comment, index: number): void {
    this.deleteCommentGQL
      .mutate({
        variables: {
          commentid: comment.id,
        },
      })
      .subscribe(
        (res) => {
          this.closeCommentModal();
          this.comments().splice(index, 1);
          this.changeDetectorRef.detectChanges();
          this.toastService.showToast('Comment deleted successfully');

          // tell parent to update comments displayed
          this.commentDeletionEvent.emit(index);
        },
        (err) => {
          console.error(err);
          this.closeCommentModal();
          this.toastService.showToast('Failed to delete comment');
        },
      );
  }

  getCommentableType(): string {
    const commentableTypeOverride = this.commentableTypeOverride();
    return commentableTypeOverride
      ? commentableTypeOverride
      : this.commentable().__typename;
  }

  getCommentTooltip(): string {
    return `View Comments (${this.comments()?.length})`;
  }
}
